/* ----------------------------------------------------------------------------
 * GCC currency display (2026-10-06).
 *
 * The store sells and checks out in AED only (cards go through Paymob, so
 * Shopify multi-currency is unavailable). For shoppers in Saudi Arabia, Qatar,
 * Bahrain, Oman and Kuwait we re-render every price marked with
 * [data-tb-fx="<fils>"] (snippets/price-as-money.liquid, snippets/fx-money.liquid)
 * as  "SAR 4,288 (AED 4,199)"  — local currency first, the real AED price kept
 * visible but smaller — and reveal a "you'll pay in AED" note in the cart and
 * under the product price (for "Buy it now", which skips the cart).
 *
 * Config comes from snippets/fx-config.liquid (#tb-fx-config): theme-setting
 * rates ("1 AED = x local"), so a rate change never needs a code change.
 * Country = window.tbGeo.resolveCountry (same source as the GCC delivery
 * promise). Unknown country, missing rate or any error → prices stay AED.
 * Re-syncs on an emirate/area change and on BFCache restore; new prices that
 * arrive later (variant change, cart drawer refresh, quick buy, search) are
 * picked up by a MutationObserver.
 * ------------------------------------------------------------------------- */
(() => {
  const configEl = document.getElementById('tb-fx-config');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }
  if (!config || config.base !== 'AED') return;

  // Only countries with a usable, positive rate take part.
  const currencies = {};
  Object.keys(config.rates || {}).forEach((country) => {
    const entry = config.rates[country];
    const rate = parseFloat(entry && entry.rate);
    if (rate > 0 && isFinite(rate)) currencies[country] = Object.assign({}, entry, { rate });
  });
  const countries = Object.keys(currencies);
  if (!countries.length) return;

  // Shopify's `t` filter HTML-escapes plain strings ("You&#39;ll") — decode once.
  function decode(text) {
    const area = document.createElement('textarea');
    area.innerHTML = String(text || '');
    return area.value;
  }
  const notes = {};
  Object.keys(config.notes || {}).forEach((type) => {
    notes[type] = decode(config.notes[type]);
  });
  countries.forEach((country) => {
    currencies[country].label = decode(currencies[country].label);
  });

  const SELECTOR = '[data-tb-fx]';
  const originals = new WeakMap(); // element → its server-rendered AED HTML
  let active = null; // currency entry for the shopper, or null for plain AED
  const numberFormats = {};

  function formatLocal(fils, currency, bare) {
    const key = currency.code;
    if (!numberFormats[key]) {
      numberFormats[key] = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: currency.decimals,
        maximumFractionDigits: currency.decimals
      });
    }
    const amount = numberFormats[key].format((fils / 100) * currency.rate);
    if (bare) return amount; // e.g. the upper end of "SAR 113–126"
    return (currency.label || currency.code) + ' ' + amount;
  }

  function apply(el) {
    if (!originals.has(el)) {
      // A copy of an already-converted price (e.g. a sticky bar cloning the
      // buy-box markup) — recover its AED HTML from the bracketed part.
      const copied = el.hasAttribute('data-tb-fx-on') && el.querySelector('.tb-fx__base');
      originals.set(el, copied ? copied.innerHTML.replace(/^\(|\)$/g, '') : el.innerHTML);
    }
    const base = originals.get(el);

    if (!active) {
      if (el.hasAttribute('data-tb-fx-on')) {
        el.innerHTML = base;
        el.removeAttribute('data-tb-fx-on');
      }
      return;
    }

    const fils = parseInt(el.getAttribute('data-tb-fx'), 10);
    if (isNaN(fils)) return;
    if (el.getAttribute('data-tb-fx-on') === active.code) return;

    const local = '<span class="tb-fx__local">' + formatLocal(fils, active, el.hasAttribute('data-tb-fx-bare')) + '</span>';
    el.innerHTML = el.hasAttribute('data-tb-fx-compact')
      ? local // local currency only — no "(AED …)" and no trailing space
      : // The plain space between the two parts is a line-break opportunity: in a
        // narrow card the "(AED …)" drops under the local price instead of
        // spilling out of the card (each part stays unbroken — nowrap).
        local + ' <span class="tb-fx__base">(' + base + ')</span>';
    el.setAttribute('data-tb-fx-on', active.code);
  }

  function applyWithin(root) {
    if (root.nodeType !== 1) return;
    if (root.matches(SELECTOR)) apply(root);
    root.querySelectorAll(SELECTOR).forEach(apply);
  }

  function updateNotes() {
    // data-tb-fx-note="cart" (cart drawer / cart page) or "pdp" (under the
    // product price — "Buy it now" goes straight to checkout, skipping the cart).
    document.querySelectorAll('[data-tb-fx-note]').forEach((note) => {
      const text = notes[note.getAttribute('data-tb-fx-note') || 'cart'];
      if (active && text) {
        note.textContent = text.replace('[currency]', active.label || active.code);
        note.hidden = false;
      } else {
        note.hidden = true;
      }
    });
  }

  function render() {
    document.documentElement.classList.toggle('tb-fx-active', !!active);
    applyWithin(document.body);
    updateNotes();
  }

  async function resolve() {
    let country = null;
    try {
      if (window.tbGeo && window.tbGeo.resolveCountry) {
        country = await window.tbGeo.resolveCountry(config.market, countries);
      }
    } catch (e) {
      country = null;
    }
    active = country ? currencies[country] : null;
    render();
  }

  // Prices that arrive after load (variant change, cart drawer, quick buy, search).
  // Our own rewrites only add children WITHOUT [data-tb-fx], so they don't loop.
  const observer = new MutationObserver((mutations) => {
    if (!active) return;
    let notesTouched = false;
    mutations.forEach((m) => {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        applyWithin(node);
        if (node.matches('[data-tb-fx-note]') || node.querySelector('[data-tb-fx-note]')) notesTouched = true;
      });
    });
    if (notesTouched) updateNotes();
  });

  function start() {
    observer.observe(document.body, { childList: true, subtree: true });
    resolve();
    const changeEvent = (window.tbGeo && window.tbGeo.EMIRATE_CHANGE_EVENT) || 'on:emirate:change';
    document.addEventListener(changeEvent, resolve);
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) resolve();
    });
  }

  // window.tbGeo comes from express-delivery.js, which theme.liquid loads before
  // this file; wait for DOMContentLoaded if it somehow isn't there yet.
  if (window.tbGeo || document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });
})();
